export type OfficeLogoType = "barangay" | "lgu";

export type OfficeLogoRecord = {
  type: OfficeLogoType;
  fileName: string;
  mimeType: AcceptedOfficeLogoMimeType;
  dataUrl: string;
  sizeBytes: number;
  updatedAt: string;
};

export type OfficeBrandingSettings = {
  activeLogoType: OfficeLogoType | null;
  barangayLogo: OfficeLogoRecord | null;
  lguLogo: OfficeLogoRecord | null;
};

export type ActiveOfficeLogo = {
  alt: string;
  dataUrl: string | null;
  fileName: string;
  isDefault: boolean;
  mimeType: AcceptedOfficeLogoMimeType | "image/png";
  src: string;
  type: OfficeLogoType | "default";
};

export type OfficeLogoValidationResult =
  | { ok: true; mimeType: AcceptedOfficeLogoMimeType }
  | { ok: false; message: string };

export type AcceptedOfficeLogoMimeType = "image/png" | "image/jpeg" | "image/webp";

export const defaultOfficeLogoPath = "/logos/RBIM.png";
export const maxOfficeLogoSizeBytes = 2 * 1024 * 1024;

const acceptedOfficeLogoMimeTypes = new Set<string>([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

const fileExtensionMimeTypes: Record<string, AcceptedOfficeLogoMimeType> = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
};

export const defaultOfficeBrandingSettings: OfficeBrandingSettings = {
  activeLogoType: null,
  barangayLogo: null,
  lguLogo: null,
};

export function normalizeOfficeBrandingSettings(
  branding?: (Partial<OfficeBrandingSettings>) | undefined,
): OfficeBrandingSettings {
  const barangayLogo = normalizeOfficeLogoRecord(branding?.barangayLogo, "barangay");
  const lguLogo = normalizeOfficeLogoRecord(branding?.lguLogo, "lgu");
  const activeLogoType = getSafeActiveLogoType(branding?.activeLogoType, barangayLogo, lguLogo);

  return {
    activeLogoType,
    barangayLogo,
    lguLogo,
  };
}

export function validateOfficeLogoFile(file: File): OfficeLogoValidationResult {
  const mimeType = getOfficeLogoMimeType(file);

  if (file.size <= 0) {
    return {
      ok: false,
      message: "The selected logo file is empty. Please choose a valid image.",
    };
  }

  if (!mimeType || !acceptedOfficeLogoMimeTypes.has(mimeType)) {
    return {
      ok: false,
      message: "Unsupported logo format. Please upload a PNG, JPG, JPEG, or WEBP image.",
    };
  }

  if (file.size > maxOfficeLogoSizeBytes) {
    return {
      ok: false,
      message: "Logo file is too large. Please upload an image that is 2 MB or smaller.",
    };
  }

  return { ok: true, mimeType };
}

export async function readOfficeLogoFile(file: File, type: OfficeLogoType): Promise<OfficeLogoRecord> {
  const validation = validateOfficeLogoFile(file);

  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const dataUrl = await readFileAsDataUrl(file);

  if (!isValidOfficeLogoDataUrl(dataUrl, validation.mimeType)) {
    throw new Error("The selected image could not be read. Please choose a different logo file.");
  }

  await ensureOfficeLogoCanDecode(dataUrl);

  return {
    type,
    fileName: file.name || `${type}-logo`,
    mimeType: validation.mimeType,
    dataUrl,
    sizeBytes: file.size,
    updatedAt: new Date().toISOString(),
  };
}

export function setOfficeLogo(
  branding: OfficeBrandingSettings,
  logo: OfficeLogoRecord,
): OfficeBrandingSettings {
  return normalizeOfficeBrandingSettings({
    ...branding,
    activeLogoType: logo.type,
    barangayLogo: logo.type === "barangay" ? logo : branding.barangayLogo,
    lguLogo: logo.type === "lgu" ? logo : branding.lguLogo,
  });
}

export function setActiveOfficeLogo(
  branding: OfficeBrandingSettings,
  type: OfficeLogoType,
): OfficeBrandingSettings {
  return normalizeOfficeBrandingSettings({
    ...branding,
    activeLogoType: getLogoByType(branding, type) ? type : branding.activeLogoType,
  });
}

export function removeOfficeLogoFromBranding(
  branding: OfficeBrandingSettings,
  type: OfficeLogoType,
): OfficeBrandingSettings {
  const nextBranding = {
    ...branding,
    barangayLogo: type === "barangay" ? null : branding.barangayLogo,
    lguLogo: type === "lgu" ? null : branding.lguLogo,
    activeLogoType: branding.activeLogoType === type ? null : branding.activeLogoType,
  };

  return normalizeOfficeBrandingSettings({
    ...nextBranding,
    activeLogoType: nextBranding.activeLogoType,
  });
}

export function resetOfficeBrandingToDefault(): OfficeBrandingSettings {
  return defaultOfficeBrandingSettings;
}

export function getActiveOfficeLogo(branding: OfficeBrandingSettings): ActiveOfficeLogo {
  const normalizedBranding = normalizeOfficeBrandingSettings(branding);
  const activeLogo = normalizedBranding.activeLogoType
    ? getLogoByType(normalizedBranding, normalizedBranding.activeLogoType)
    : null;

  if (activeLogo) {
    return {
      alt: activeLogo.type === "barangay" ? "Barangay logo" : "LGU logo",
      dataUrl: activeLogo.dataUrl,
      fileName: activeLogo.fileName,
      isDefault: false,
      mimeType: activeLogo.mimeType,
      src: activeLogo.dataUrl,
      type: activeLogo.type,
    };
  }

  return {
    alt: "Default RBIM logo",
    dataUrl: null,
    fileName: "RBIM.png",
    isDefault: true,
    mimeType: "image/png",
    src: defaultOfficeLogoPath,
    type: "default",
  };
}

export function getOfficeLogoLabel(type: OfficeLogoType | "default") {
  if (type === "barangay") {
    return "Barangay Logo";
  }

  if (type === "lgu") {
    return "LGU Logo";
  }

  return "Default RBIM Logo";
}

export function formatOfficeLogoFileSize(sizeBytes: number) {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }

  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 102.4) / 10} KB`;
  }

  return `${Math.round(sizeBytes / 1024 / 102.4) / 10} MB`;
}

export function getOfficeLogoMimeTypeFromDataUrl(dataUrl: string): AcceptedOfficeLogoMimeType | null {
  const match = /^data:(image\/(?:png|jpeg|webp));base64,/i.exec(dataUrl.trim());
  const mimeType = match?.[1]?.toLowerCase();

  return isAcceptedMimeType(mimeType) ? mimeType : null;
}

function normalizeOfficeLogoRecord(
  record: unknown,
  type: OfficeLogoType,
): OfficeLogoRecord | null {
  if (!record || typeof record !== "object") {
    return null;
  }

  const candidate = record as Partial<OfficeLogoRecord>;
  const mimeType = isAcceptedMimeType(candidate.mimeType) ? candidate.mimeType : null;
  const dataUrl = typeof candidate.dataUrl === "string" ? candidate.dataUrl : "";

  if (!mimeType || !isValidOfficeLogoDataUrl(dataUrl, mimeType)) {
    return null;
  }

  return {
    type,
    fileName: String(candidate.fileName || `${type}-logo`),
    mimeType,
    dataUrl,
    sizeBytes: Number.isFinite(candidate.sizeBytes) ? Number(candidate.sizeBytes) : 0,
    updatedAt: String(candidate.updatedAt || new Date().toISOString()),
  };
}

function getSafeActiveLogoType(
  activeLogoType: unknown,
  barangayLogo: OfficeLogoRecord | null,
  lguLogo: OfficeLogoRecord | null,
): OfficeLogoType | null {
  if (activeLogoType === "barangay" && barangayLogo) {
    return "barangay";
  }

  if (activeLogoType === "lgu" && lguLogo) {
    return "lgu";
  }

  return null;
}

function getLogoByType(branding: OfficeBrandingSettings, type: OfficeLogoType) {
  return type === "barangay" ? branding.barangayLogo : branding.lguLogo;
}

function getOfficeLogoMimeType(file: File): AcceptedOfficeLogoMimeType | null {
  const explicitMimeType = file.type.toLowerCase();

  if (isAcceptedMimeType(explicitMimeType)) {
    return explicitMimeType;
  }

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";

  return fileExtensionMimeTypes[extension] ?? null;
}

function isAcceptedMimeType(mimeType: unknown): mimeType is AcceptedOfficeLogoMimeType {
  return typeof mimeType === "string" && acceptedOfficeLogoMimeTypes.has(mimeType.toLowerCase());
}

function isValidOfficeLogoDataUrl(dataUrl: string, mimeType: AcceptedOfficeLogoMimeType) {
  return dataUrl.trim().startsWith(`data:${mimeType};base64,`);
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("The selected logo file could not be read."));
    reader.readAsDataURL(file);
  });
}

function ensureOfficeLogoCanDecode(dataUrl: string) {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        resolve();
        return;
      }

      reject(new Error("The selected file is not a valid image. Please choose a different logo file."));
    };
    image.onerror = () => {
      reject(new Error("The selected file is corrupted or could not be decoded as an image."));
    };
    image.src = dataUrl;
  });
}
